/**
 * Sort messages by createdAt value
 * @param a {Object}
 * @param b {Object}
 * @return {number}
 */
function messageDateComparator(a, b) {
  return new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf();
}

/**
 * Get function to sort objects by field or fields
 * @param predicate {String|String[]}
 * @param customAttrGetter {function}
 * @return {(function(*, *): number)}
 */
function fieldComparator(predicate, customAttrGetter) {
  const getter = customAttrGetter ? customAttrGetter : (a, b) => a[b];
  if (Array.isArray(predicate)) {
    return (a, b) => {
      for (const subPredicate of predicate) {
        if (getter(a, subPredicate) === getter(b, subPredicate)) {
          continue;
        }
        return getter(a, subPredicate) > getter(b, subPredicate) ? 1 : -1;
      }
      return 1;
    };
  }
  return (a, b) => (getter(a, predicate) > getter(b, predicate) ? 1 : -1);
}

/**
 * Checks if user linked to message
 * @param msg {Object}
 * @param userUUID {String}
 * @return {boolean}
 */
function messageBelongsToUserFilter(msg, userUUID) {
  return userUUID === msg.userUUID || msg.recipientUUID === userUUID;
}

/**
 * Generate UUID (v.4)
 * @return {string}
 */
function getUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Check is it own message from stream.
 * @param message {Object}
 * @param user_uuid {String}
 * @return {boolean}
 */
function isOwnMessageFromStream(message, user_uuid) {
  return !!(message.isFromStream && message.userUUID === user_uuid);
}

/**
 * Update a message if a profanity filter is applied.
 * @param message {Object}
 * @param message_array {Array}
 */
function updateMessage(message, message_array) {
  if (message.isProfanityFilterApplied) {
    let messageToEdit = message_array.find(e => e.messageUUID === message.messageUUID);
    messageToEdit.messageBody = message.messageBody;
  }
}

/**
 * Prepare and clear search attributes
 * @param words {String[]}
 * @return {String[]}
 */
function prepareSearchAttributes(words) {
  return words
    .filter(Boolean)
    .map(word => word.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Search over given list of attributes
 * @param searchQuery {String}
 * @param searchAttributes {String[]}
 * @return {Boolean}
 */
function searchOverMultipleAttributes(searchQuery, searchAttributes) {
  const searchableAttributes = prepareSearchAttributes(searchAttributes);
  const searchPredicates = prepareSearchAttributes(searchQuery.split(" "));

  return searchPredicates.every(predicate => {
    return searchableAttributes.find(attr => attr.includes(predicate));
  });
}

/**
 * Get user attrs from user or profile
 * @param {Object} user
 * @param {String|String[]} nameAttr
 * @param keepOriginal {Boolean}
 * @return {String|Array}
 */
function getUserProperty(user, nameAttr, keepOriginal) {
  function _lowerStrings(value) {
    if (keepOriginal) {
      return value;
    }
    return typeof value === "string" ? value.toLowerCase() : value;
  }

  if (Array.isArray(nameAttr)) {
    return nameAttr.map(x => {
      return user.profile && user.profile[x] ? _lowerStrings(user.profile[x]) : _lowerStrings(user[x]);
    });
  }
  return user.profile && user.profile[nameAttr] ? _lowerStrings(user.profile[nameAttr]) : _lowerStrings(user[nameAttr]);
}

/**
 * Debounce given function
 * @param fn {function}
 * @param delay {Number}
 * @return {function}
 */
function debounce(fn, delay) {
  var timeoutID = null;
  return function() {
    clearTimeout(timeoutID);
    let args = arguments;
    let that = this;
    timeoutID = setTimeout(function() {
      fn.apply(that, args);
    }, delay);
  };
}
async function checkImageExists(imageUrl) {
  let response = await fetch(imageUrl, {
    mode: "cors",
    headers: {
      "Access-Control-Allow-Origin": "*"
    }
  });
  return response.status === 200 || response.status !== 403;
}

function getThemeColor(colorName) {
  return {
    color: getComputedStyle(document.documentElement)
      .getPropertyValue(colorName)
      .trim()
  };
}

function hexToRgb(hex) {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
}

function getPaginationObject(perPage, pageNumber = 0) {
  return {
    start: pageNumber,
    number: perPage
  };
}

function shadeColor(color, percent) {
  var R = parseInt(color.substring(1, 3), 16);
  var G = parseInt(color.substring(3, 5), 16);
  var B = parseInt(color.substring(5, 7), 16);

  R = parseInt((R * (100 + percent)) / 100);
  G = parseInt((G * (100 + percent)) / 100);
  B = parseInt((B * (100 + percent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  var RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16);
  var GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16);
  var BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16);

  return "#" + RR + GG + BB;
}

export {
  messageDateComparator,
  fieldComparator,
  messageBelongsToUserFilter,
  getUUID,
  isOwnMessageFromStream,
  updateMessage,
  searchOverMultipleAttributes,
  getUserProperty,
  debounce,
  prepareSearchAttributes,
  checkImageExists,
  getThemeColor,
  hexToRgb,
  getPaginationObject,
  shadeColor
};
